/**
 * Email Configuration Page
 * Feature: System Administration Interface (001-admin-settings)
 */
import React, { useState, useEffect } from 'react';
import { Mail, Save, TestTube, Eye, EyeOff } from 'lucide-react';
import { getEmailConfiguration, updateEmailConfiguration } from '../../api/email-config';
import type { EmailConfiguration, EmailConfigurationUpdate, EmailProvider } from '../../types/email-config';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Select from '../../components/Select';
import EmailTestModal from '../../components/admin/EmailTestModal';

const EmailConfigPage: React.FC = () => {
  const [config, setConfig] = useState<EmailConfiguration | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isTestModalOpen, setIsTestModalOpen] = useState<boolean>(false);

  // Form state
  const [provider, setProvider] = useState<EmailProvider>('azure_communication_services');
  const [smtpHost, setSmtpHost] = useState<string>('');
  const [smtpPort, setSmtpPort] = useState<number>(587);
  const [smtpUsername, setSmtpUsername] = useState<string>('');
  const [smtpPassword, setSmtpPassword] = useState<string>('');
  const [smtpUseTls, setSmtpUseTls] = useState<boolean>(true);
  const [showSmtpPassword, setShowSmtpPassword] = useState<boolean>(false);
  const [azureConnectionString, setAzureConnectionString] = useState<string>('');
  const [showAzureConnectionString, setShowAzureConnectionString] = useState<boolean>(false);
  const [senderEmail, setSenderEmail] = useState<string>('');
  const [senderName, setSenderName] = useState<string>('');

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getEmailConfiguration();
      setConfig(data);

      // Populate form fields
      setProvider(data.provider);
      setSmtpHost(data.smtp_host || '');
      setSmtpPort(data.smtp_port || 587);
      setSmtpUsername(data.smtp_username || '');
      setSmtpUseTls(data.smtp_use_tls);
      setSenderEmail(data.sender_email);
      setSenderName(data.sender_name);
      // Passwords/secrets are not returned by API
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load email configuration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updates: EmailConfigurationUpdate = {
        provider,
        sender_email: senderEmail,
        sender_name: senderName
      };

      if (provider === 'smtp') {
        updates.smtp_host = smtpHost || null;
        updates.smtp_port = smtpPort || null;
        updates.smtp_username = smtpUsername || null;
        updates.smtp_use_tls = smtpUseTls;
        // Only include password if user entered a new one
        if (smtpPassword) {
          updates.smtp_password = smtpPassword;
        }
        // Clear Azure fields when switching to SMTP
        if (azureConnectionString) {
          updates.azure_connection_string = null;
        }
      } else {
        // Azure Communication Services
        // Only include connection string if user entered a new one
        if (azureConnectionString) {
          updates.azure_connection_string = azureConnectionString;
        }
        // Clear SMTP fields when switching to Azure
        updates.smtp_host = null;
        updates.smtp_port = null;
        updates.smtp_username = null;
        if (smtpPassword) {
          updates.smtp_password = null;
        }
      }

      const updated = await updateEmailConfiguration(updates);
      setConfig(updated);
      setSuccessMessage('Email configuration updated successfully');

      // Clear password fields after save
      setSmtpPassword('');
      setAzureConnectionString('');

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update email configuration';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config) {
      setProvider(config.provider);
      setSmtpHost(config.smtp_host || '');
      setSmtpPort(config.smtp_port || 587);
      setSmtpUsername(config.smtp_username || '');
      setSmtpPassword('');
      setSmtpUseTls(config.smtp_use_tls);
      setAzureConnectionString('');
      setSenderEmail(config.sender_email);
      setSenderName(config.sender_name);
    }
  };

  if (loading) {
    return (
      <div className="loading-container min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="p-6">
        <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30">
          <p className="text-error">{error}</p>
          <Button variant="outline" onClick={loadConfiguration} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-4 md:px-6 py-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400" />
          Email Configuration
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
          Configure email delivery settings for notifications and system emails
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 rounded-lg p-4 bg-success/10 dark:bg-success/20 border border-success/30">
          <p className="text-success">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30">
          <p className="text-error">{error}</p>
        </div>
      )}

      <Card variant="elevated">
        <Card.Body>
          <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Email Provider <span className="text-error">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${provider === 'azure_communication_services' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-primary-500'}`}>
                  <input
                    type="radio"
                    name="provider"
                    value="azure_communication_services"
                    checked={provider === 'azure_communication_services'}
                    onChange={(e) => setProvider(e.target.value as EmailProvider)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Azure Communication Services
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Microsoft's enterprise email service (Recommended)
                    </div>
                  </div>
                </label>

                <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${provider === 'smtp' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-primary-500'}`}>
                  <input
                    type="radio"
                    name="provider"
                    value="smtp"
                    checked={provider === 'smtp'}
                    onChange={(e) => setProvider(e.target.value as EmailProvider)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">SMTP</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Standard email protocol (Gmail, SendGrid, etc.)
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* SMTP Configuration */}
            {provider === 'smtp' && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">SMTP Settings</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id="smtp-host"
                    type="text"
                    label="SMTP Host"
                    required
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.gmail.com"
                  />

                  <Select
                    id="smtp-port"
                    label="SMTP Port"
                    required
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value, 10))}
                  >
                    <option value={25}>25 (Plain)</option>
                    <option value={465}>465 (SSL/TLS)</option>
                    <option value={587}>587 (STARTTLS)</option>
                  </Select>
                </div>

                <Input
                  id="smtp-username"
                  type="text"
                  label="SMTP Username"
                  required
                  value={smtpUsername}
                  onChange={(e) => setSmtpUsername(e.target.value)}
                  placeholder="your-email@gmail.com"
                />

                <Input
                  id="smtp-password"
                  type={showSmtpPassword ? 'text' : 'password'}
                  label={smtpPassword ? 'SMTP Password' : 'SMTP Password (leave blank to keep existing)'}
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder="Enter new password or leave blank"
                  helperText="Password is encrypted before storage using Fernet encryption"
                  rightIcon={showSmtpPassword ? EyeOff : Eye}
                  onRightIconClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  rightIconLabel={showSmtpPassword ? 'Hide password' : 'Show password'}
                />

                <div className="flex items-center">
                  <input
                    id="smtp-use-tls"
                    type="checkbox"
                    checked={smtpUseTls}
                    onChange={(e) => setSmtpUseTls(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="smtp-use-tls" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Use TLS/SSL encryption (Recommended)
                  </label>
                </div>
              </div>
            )}

            {/* Azure Communication Services Configuration */}
            {provider === 'azure_communication_services' && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">Azure Communication Services Settings</h3>

                <Input
                  id="azure-connection-string"
                  type={showAzureConnectionString ? 'text' : 'password'}
                  label={azureConnectionString ? 'Connection String' : 'Connection String (leave blank to keep existing)'}
                  value={azureConnectionString}
                  onChange={(e) => setAzureConnectionString(e.target.value)}
                  placeholder="endpoint=https://...;accesskey=..."
                  helperText="Connection string is encrypted before storage using Fernet encryption"
                  className="font-mono text-sm"
                  rightIcon={showAzureConnectionString ? EyeOff : Eye}
                  onRightIconClick={() => setShowAzureConnectionString(!showAzureConnectionString)}
                  rightIconLabel={showAzureConnectionString ? 'Hide connection string' : 'Show connection string'}
                />

                <div className="rounded-lg p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
                  <p className="text-sm text-primary-800 dark:text-primary-300">
                    <strong>Note:</strong> Azure Communication Services provides enterprise-grade email delivery with
                    built-in spam filtering, bounce handling, and compliance features.
                  </p>
                </div>
              </div>
            )}

            {/* Sender Configuration */}
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Sender Information</h3>

              <Input
                id="sender-email"
                type="email"
                label="Sender Email"
                required
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="noreply@yourcompany.com"
                helperText='This email address will appear in the "From" field of all system emails'
              />

              <Input
                id="sender-name"
                type="text"
                label="Sender Name"
                required
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Project Management System"
                helperText="Display name that recipients will see"
              />
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                leftIcon={TestTube}
                onClick={() => setIsTestModalOpen(true)}
                type="button"
                disabled={saving}
                className="w-full sm:w-auto"
              >
                Send Test Email
              </Button>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <Button variant="outline" onClick={handleReset} type="button" disabled={saving} className="w-full sm:w-auto">
                  Reset
                </Button>
                <Button variant="primary" type="submit" isLoading={saving} leftIcon={Save} disabled={saving} className="w-full sm:w-auto">
                  {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </div>
          </form>
        </Card.Body>
      </Card>

      {/* Security Notice */}
      <div className="mt-6 rounded-lg p-4 bg-warning/10 dark:bg-warning/20 border border-warning/30">
        <h4 className="font-semibold text-warning mb-2">
          🔐 Security Notice - Development Mode
        </h4>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Passwords and connection strings are encrypted using Fernet encryption before storage.
          Current implementation uses development-grade encryption.
          For production deployment, configure Azure Key Vault integration for enterprise-grade security.
          See <code className="bg-warning/20 px-1 rounded">docs/ENCRYPTION_UPGRADE.md</code> for upgrade instructions.
        </p>
      </div>

      {/* Email Test Modal */}
      <EmailTestModal isOpen={isTestModalOpen} onClose={() => setIsTestModalOpen(false)} />
    </div>
  );
};

export default EmailConfigPage;
